// Component library for water quality field observations - 17 mobile-optimized components
import React from 'react';
import {
  Pencil, Hash, ChevronDownSquare, FileText, ToggleLeft, Calendar,
  CheckSquare, CircleDot, Tag, Camera, MapPin, PenTool,
  QrCode, Barcode, Repeat, Minus, Heading, FlaskConical, Mic,
} from 'lucide-react';

export const COMPONENT_LIBRARY = [
    // Input Components (15 total)
    {
        id: 'input',
        type: 'input',
        label: 'Text Input',
        icon: <Pencil size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Text Field',
            name: 'text_field',
            placeholder: 'Enter text...',
            required: false,
            type: 'text'
        }
    },
    {
        id: 'sample_id',
        type: 'input',
        label: 'Sample ID',
        icon: <FlaskConical size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Sample ID',
            name: 'sample_id',
            placeholder: 'Enter sample ID...',
            required: false,
            type: 'text',
            isSampleId: true,
        }
    },
    {
        id: 'number',
        type: 'number',
        label: 'Number Input',
        icon: <Hash size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Number Field',
            name: 'number_field',
            placeholder: 'Enter number...',
            required: false,
            type: 'number',
            min: null,
            max: null
        }
    },
    {
        id: 'dropdown',
        type: 'select',
        label: 'Dropdown',
        icon: <ChevronDownSquare size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Select Field',
            name: 'select_field',
            required: false,
            options: [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'maybe', label: 'Maybe' }
            ]
        }
    },
    {
        id: 'textarea',
        type: 'textarea',
        label: 'Text Area',
        icon: <FileText size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Text Area',
            name: 'text_area',
            placeholder: 'Enter detailed text...',
            required: false,
            rows: 3
        }
    },
    {
        id: 'toggle',
        type: 'toggle',
        label: 'Toggle Switch',
        icon: <ToggleLeft size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Toggle Field',
            name: 'toggle_field',
            defaultValue: false
        }
    },
    {
        id: 'datetime',
        type: 'datetime',
        label: 'Date / Time',
        icon: <Calendar size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Date / Time Field',
            name: 'datetime_field',
            required: false,
            type: 'datetime',
            precision: 'datetime',
            autoCapture: false
        }
    },
    {
        id: 'checkbox_group',
        type: 'checkbox_group',
        label: 'Checkbox Group',
        icon: <CheckSquare size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Checkbox Group',
            name: 'checkbox_group',
            required: false,
            options: [
                { value: 'red', label: 'Red' },
                { value: 'blue', label: 'Blue' },
                { value: 'green', label: 'Green' }
            ]
        }
    },
    {
        id: 'radio',
        type: 'radio',
        label: 'Radio Group',
        icon: <CircleDot size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Radio Group',
            name: 'radio_group',
            required: false,
            options: [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'maybe', label: 'Maybe' }
            ]
        }
    },
    {
        id: 'tags',
        type: 'tags',
        label: 'Tag Field',
        icon: <Tag size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Tag Field',
            name: 'tag_field',
            placeholder: 'Type and press Enter...',
            required: false,
            options: [],
            allowCustom: true,
            maxTags: null
        }
    },
    {
        id: 'camera',
        type: 'image_camera',
        label: 'Camera Capture',
        icon: <Camera size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Camera Field',
            name: 'camera_field',
            required: false,
            placeholder: 'Tap to capture photo...',
            cameraOnly: true
        }
    },
    {
        id: 'geolocation',
        type: 'geolocation',
        label: 'Location Capture',
        icon: <MapPin size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Location Field',
            name: 'location_field',
            required: false,
            placeholder: 'Tap to capture location...',
            accuracy: 'high'
        }
    },
    {
        id: 'drawing',
        type: 'drawing',
        label: 'Drawing Field',
        icon: <PenTool size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Drawing Field',
            name: 'drawing_field',
            required: false,
            placeholder: 'Tap to draw or add signature...'
        }
    },
    {
        id: 'voice',
        type: 'voice',
        label: 'Voice Recording',
        icon: <Mic size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Voice Recording',
            name: 'voice_recording',
            required: false,
            placeholder: 'Tap to record',
            maxDurationSeconds: 300,
            quality: 'medium',
            enableTranscription: true,
            transcriptionLanguage: 'en-US',
        }
    },
    {
        id: 'qr_scanner',
        type: 'qr_scanner',
        label: 'QR Code Scanner',
        icon: <QrCode size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'QR Code Field',
            name: 'qr_code_field',
            required: false,
            placeholder: 'Tap to scan QR code...',
            validationRules: {
                rules: [],
                allowEmpty: true
            }
        }
    },
    {
        id: 'barcode_scanner',
        type: 'barcode_scanner',
        label: 'Barcode Scanner',
        icon: <Barcode size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Barcode Field',
            name: 'barcode_field',
            required: false,
            placeholder: 'Tap to scan barcode...',
            validationRules: {
                rules: [],
                allowEmpty: true
            },
            barcodeFormat: 'any'
        }
    },
    {
        id: 'repeated_form',
        type: 'repeated_form',
        label: 'Repeated Form',
        icon: <Repeat size={16} />,
        category: 'inputs',
        defaultProps: {
            label: 'Repeated Form Field',
            name: 'repeated_form_field',
            required: false,
            configuration: {
                repeatedFormSchemaId: null,
                minInstances: 0,
                maxInstances: 10,
                repeatedFormDisplayName: ''
            }
        }
    },

    // Layout Components (2 total)
    {
        id: 'divider',
        type: 'divider',
        label: 'Divider',
        icon: <Minus size={16} />,
        category: 'layout',
        defaultProps: {
            name: 'divider',
            style: 'solid',
            margin: 'medium'
        }
    },
    {
        id: 'header',
        type: 'header',
        label: 'Header',
        icon: <Heading size={16} />,
        category: 'layout',
        defaultProps: {
            name: 'section_header',
            text: 'Section Header',
            level: 'h3',
            align: 'left'
        }
    }
];
